import { ChangeDetectorRef, OnDestroy, Pipe, PipeTransform } from '@angular/core';
import memoize from '@github/memoize/decorator';
import { DateInput, toDate } from '@macs-frontend/core/utils/date-utils';
import { Company } from '@macs-frontend/web-macs/shared';
import * as fns from 'date-fns/fp';
import { boolean as B, function as F, option as O } from 'fp-ts';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CurrentCompanyService } from '../../../core';
import { TimeDisplays, TimeFormats } from '../../../core/utils/consts';

@Pipe({ name: 'time', pure: false })
export class TimePipe implements PipeTransform, OnDestroy {
  private timeFormat = TimeFormats.HOURS_24;
  private timeDisplay = TimeDisplays.HH_MM;
  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    currentCompanyService: CurrentCompanyService,
    private cdr: ChangeDetectorRef,
  ) {
    currentCompanyService.currentCompany$
      .pipe(takeUntil(this.destroy$))
      .subscribe((company: Company) => {
        if (company && company.timeFormat) {
          this.timeFormat = company.timeFormat as TimeFormats;
        }
        if (company && company.timeDisplay) {
          this.timeDisplay = company.timeDisplay as TimeDisplays;
        }
        this.cdr.markForCheck();
      });
  }

  @memoize()
  public transform<TValue extends DateInput | string | null | undefined>(
    value?: TValue,
    // Parse format makes sense only if value is string not DateInput or nullable.
    parseFormat?: TValue extends DateInput | null | undefined ? undefined : string,
    changeTimeFormat = true,
  ): string {
    const { timeDisplay, timeFormat } = this;
    const timeDisplaySep = timeDisplay === TimeDisplays.HH_MM ? ':' : '.';
    const emptyTime = `--${timeDisplaySep}--`;

    return F.pipe(
      value,
      O.fromNullable,
      O.fold(
        () => emptyTime,
        value =>
          F.pipe(
            typeof parseFormat === 'string'
              ? F.pipe({ value: value as string, parseFormat }, parseDateWithFormat)
              : F.pipe(value as DateInput, toDate),
            formatTime,
          ),
      ),
    );

    function parseDateWithFormat({
      value,
      parseFormat,
    }: {
      value: string;
      parseFormat: string;
    }): Date {
      return F.pipe(value, fns.parse(new Date())(parseFormat));
    }

    function formatTime(date: Date): string {
      return F.pipe(
        date,
        O.fromPredicate(date => F.pipe(date, fns.isValid)),
        O.fold(
          () => 'Invalid date',
          fns.format(
            F.pipe(
              !changeTimeFormat || timeFormat === TimeFormats.HOURS_24,
              B.fold(
                () => `h${timeDisplaySep}mm a`,
                () => `H${timeDisplaySep}mm`,
              ),
            ),
          ),
        ),
      );
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
